import { Check, Pencil, UserPlus } from 'lucide-react'
import { toast } from 'sonner'

import { FormEvent, KeyboardEvent, useEffect, useState } from 'react'

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, Input, Label, PosButton, Spinner } from '@/components/ui'
import { useSession } from '@/contexts/SessionContext'
import { useCreateCustomer, useCustomers, useUpdateCustomer } from '@/hooks/features/customers'
import { useDebounce } from '@/hooks/ui/useDebounce'
import { nextInStoreCode } from '@/lib/inStoreCode'
import { cn } from '@/lib/utils'
import { usePrinter } from '@/services/printer'
import { PosModel } from '@/types'
import { initials } from '@/utils'

interface CustomerDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  selected?: PosModel.Customer
  onSelect: (customer: PosModel.Customer) => void
}

/** Search by name or phone, arrows + Enter to pick, add a new customer or edit one without leaving the sale. */
export const CustomerDialog = ({ open, onOpenChange, selected, onSelect }: CustomerDialogProps) => {
  const [search, setSearch] = useState('')
  const [cursor, setCursor] = useState(0)
  const [creating, setCreating] = useState(false)
  // The customer being edited on the device (synced like a new one); the same form as a new customer.
  const [editing, setEditing] = useState<PosModel.Customer | null>(null)
  const debounced = useDebounce(search)
  const { data, isFetching } = useCustomers(debounced, open)
  const create = useCreateCustomer()
  const update = useUpdateCustomer()
  const [card, setCard] = useState('')
  // The code "New card" made: printed when the customer is saved with it.
  const [newCard, setNewCard] = useState('')
  const { printCard } = usePrinter()
  const { can, stage } = useSession()
  const items = data?.items ?? []

  useEffect(() => {
    if (open) {
      setSearch('')
      setCreating(false)
      setEditing(null)
    }
  }, [open])
  useEffect(() => setCursor(0), [debounced])

  const pick = (c: PosModel.Customer) => {
    onSelect(c)
    onOpenChange(false)
  }

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown') setCursor((i) => Math.min(i + 1, items.length - 1))
    else if (e.key === 'ArrowUp') setCursor((i) => Math.max(i - 1, 0))
    else if (e.key === 'Enter' && items[cursor]) pick(items[cursor])
    else return
    e.preventDefault()
  }

  const onCreate = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    const body = {
      name: String(form.get('name')).trim(),
      phone: String(form.get('phone')).trim(),
      email: String(form.get('email') || '').trim() || undefined,
      barcode: card.trim() || undefined,
    }
    if (editing) {
      update.mutate(
        { id: editing._id, body },
        {
          onSuccess: (c) => {
            toast.success(`${c.name} saved`)
            if (newCard && c.barcode === newCard) printCard({ kind: 'customer', name: c.name, barcode: newCard })
            if (selected?._id === c._id) onSelect(c)
            setEditing(null)
          },
          onError: (err) => toast.error(err.message),
        }
      )
      return
    }
    create.mutate(
      body,
      {
        onSuccess: (c) => {
          toast.success(`${c.name} added`)
          if (newCard && c.barcode === newCard) printCard({ kind: 'customer', name: c.name, barcode: newCard })
          pick(c)
        },
        onError: (err) => toast.error(err.message),
      }
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>{editing ? `Edit ${editing.name}` : creating ? 'New customer' : 'Choose customer'}</DialogTitle>
          <DialogDescription>
            {creating || editing ? 'Phone number is used to find them next time.' : 'Search by name, phone or card.'}
          </DialogDescription>
        </DialogHeader>

        {creating || editing ? (
          <form key={editing?._id ?? 'new'} onSubmit={onCreate} className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="c-name">Name</Label>
              <Input id="c-name" name="name" required autoFocus defaultValue={editing ? editing.name : /\d/.test(search) ? '' : search} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="c-phone">Phone</Label>
              <Input id="c-phone" name="phone" required inputMode="tel" pattern="01\d{9}" title="11-digit mobile number starting with 01" defaultValue={editing ? editing.phone : /\d/.test(search) ? search : ''} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="c-email">Email (optional)</Label>
              <Input id="c-email" name="email" type="email" defaultValue={editing?.email ?? ''} />
            </div>
            {stage !== 'demo' && (
              <div className="space-y-1.5">
                <Label htmlFor="c-card">Customer card (optional)</Label>
                <div className="flex gap-2">
                  <Input
                    id="c-card"
                    value={card}
                    placeholder="Scan a card"
                    onCameraScan={setCard}
                    onChange={(e) => setCard(e.target.value)}
                    // A scanner ends with Enter; it must not submit the form.
                    onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
                  />
                  <PosButton
                    type="button"
                    variant="secondary"
                    onClick={() =>
                      nextInStoreCode('24')
                        .then((code) => {
                          setCard(code)
                          setNewCard(code)
                        })
                        .catch((err: Error) => toast.error(err.message))
                    }
                  >
                    New card
                  </PosButton>
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <PosButton type="button" variant="secondary" onClick={() => (setCreating(false), setEditing(null))}>
                Back
              </PosButton>
              <PosButton type="submit" disabled={create.isPending || update.isPending}>
                {(create.isPending || update.isPending) && <Spinner className="size-4" />} {editing ? 'Save' : 'Save and select'}
              </PosButton>
            </div>
          </form>
        ) : (
          <>
            <div className="relative">
              <Input autoFocus value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={onKeyDown} placeholder="Name or 01XXXXXXXXX" onCameraScan={setSearch} className="h-11 text-base" />
              {isFetching && <Spinner className="absolute right-3 top-1/2 size-4 -translate-y-1/2" />}
            </div>
            <ul className="-mx-2 max-h-72 overflow-y-auto" role="listbox">
              {items.map((c, i) => (
                <li key={c._id} role="option" aria-selected={i === cursor} className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => pick(c)}
                    onMouseEnter={() => setCursor(i)}
                    className={cn('flex w-full items-center gap-3 rounded-md px-2 py-2 text-left', i === cursor && 'bg-subdued')}
                  >
                    <span className="grid size-9 place-items-center rounded-full bg-fill text-xs font-semibold">{initials(c.name)}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-ink">{c.name}</span>
                      <span className="block text-xs text-muted-foreground">
                        {c.phone} · {c.loyalty_points} pts{c.tier ? ` · ${c.tier.name} ${c.tier.discount_percentage}% off` : ''}
                        {c.price_book ? ` · ${c.price_book} prices` : ''}
                      </span>
                    </span>
                    {selected?._id === c._id && <Check className="size-4 text-success" />}
                  </button>
                  {stage !== 'demo' && can('customers:update') && (
                    <PosButton
                      variant="ghost"
                      size="icon"
                      aria-label={`Edit ${c.name}`}
                      onClick={() => (setCard(c.barcode ?? ''), setNewCard(''), setEditing(c))}
                    >
                      <Pencil />
                    </PosButton>
                  )}
                </li>
              ))}
              {!items.length && !isFetching && <li className="px-2 py-6 text-center text-sm text-muted-foreground">No customer matches “{debounced}”.</li>}
            </ul>
            {can('customers:create') && (
              <PosButton variant="outline" size="touch" className="w-full" onClick={() => (setCard(''), setNewCard(''), setCreating(true))}>
                <UserPlus /> New customer
              </PosButton>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
